<template>
  <div>
    <v-container
      fluid
      class="d-flex justify-center align-center flex-wrap"
      style="position: relative"
    >
      <v-row>
        <v-spacer></v-spacer>
        <Search-box
          :emitDestination="searchEmitDestination"
          searchPlaceholder="Pretraži firme"
        ></Search-box>
      </v-row>

      <v-row
        class="justify-center fill-height ma-0 pa-0"
        style="position: absolute"
      >
        <v-card
          autofocus
          class="glass-card align-center ma-5"
          v-for="(company, idx) in sortedCompanies"
          :key="company.id"
          hover
          height="340"
          width="280"
          @click="openCompany(company)"
          @keypress.enter.stop="openCompany(company)"
          :ref="`company${idx}`"
        >
          <!-- Glass morphism header with logo/icon -->
          <div class="glass-header d-flex flex-column align-center justify-center">
            <div class="header-gloss"></div>
            <div class="logo-wrapper">
              <v-img
                v-if="company.logo"
                :src="company.logo"
                :lazy-src="require('@/assets/unknown_profile.jpg')"
                contain
                height="80"
                width="80"
                class="company-logo"
              />
              <v-icon v-else size="64" color="white">mdi-domain</v-icon>
            </div>

            <!-- Status badge -->
            <div class="status-badge" :class="getCompanyStatusClass(company.status)">
              {{ translateStatus(company.status) }}
            </div>

            <!-- Certificate indicator -->
            <div class="certificate-badge" :class="company.certificate ? 'cert-active' : 'cert-inactive'">
              <v-icon small color="white">mdi-certificate</v-icon>
              <span class="ml-1">{{ company.certificate ? 'Certifikat' : 'Bez certifikata' }}</span>
            </div>
          </div>

          <!-- Content section -->
          <div class="glass-content pa-4">
            <div class="content-text">
              <h3 class="company-name mb-3">{{ company.name }}</h3>

              <div v-if="company.oib" class="info-row d-flex align-center mb-2">
                <v-icon size="15" class="mr-2 info-icon">mdi-card-account-details-outline</v-icon>
                <span class="info-text">OIB: {{ company.oib }}</span>
              </div>

              <div v-if="company.city" class="info-row d-flex align-center">
                <v-icon size="15" class="mr-2 info-icon">mdi-map-marker-outline</v-icon>
                <span class="info-text">{{ company.city }}</span>
              </div>
            </div>

            <div class="arrow-indicator">
              <v-icon size="20">mdi-arrow-right</v-icon>
            </div>
          </div>

          <!-- Actions footer -->
          <v-divider class="glass-divider"></v-divider>
          <v-card-actions class="glass-actions pa-3">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-on="on"
                  v-bind="attrs"
                  @click.stop="addCertificate(company)"
                  @keypress.enter.stop="addCertificate(company)"
                  icon
                  small
                  class="cert-btn"
                >
                  <v-icon :color="company.certificate ? 'success' : 'error'" small
                    >mdi-certificate</v-icon
                  >
                </v-btn>
              </template>
              <span>{{
                company.certificate
                  ? "Uspješno dodan certifikat za fiskalizaciju"
                  : "Certifikat za fiskalizaciju nije dodan"
              }}</span>
            </v-tooltip>
            <v-spacer></v-spacer>
            <v-menu offset-y nudge-left="85" v-if="!hideMenu">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @keypress.enter.stop=""
                  class="menu-btn"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense class="glass-menu">
                <v-list-item
                  v-for="(item, index) in companyOptions"
                  :key="index"
                  style="cursor: pointer"
                  @click="`${item.action(company)}`"
                  @keypress.enter.prevent="`${item.action(company)}`"
                >
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-card-actions>
        </v-card>
        <div class="add-dashed-card ma-5" style="height: 340px; width: 280px;" @click="addCompany">
          <v-icon class="add-dashed-icon">mdi-plus</v-icon>
          <span class="add-dashed-text">Dodaj novu firmu</span>
        </div>
      </v-row>
    </v-container>
    <edit-company
      ref="editCompany"
      @editSuccess="refreshEditCompany"
    ></edit-company>
    <edit-receipt-settings
      ref="editReceiptSettings"
      @editSuccess="refreshEditCompany"
    ></edit-receipt-settings>
    <certificate-upload ref="certificateUpload"></certificate-upload>
    <setup-wizard ref="setupWizard"></setup-wizard>
  </div>
</template>
<script>
import { auth, df } from '@/plugins/firebase'
import { clone } from '@/plugins/utils'
import state from '@/state'
import applicationSettings from '@/mixins/applicationSettings'
import EventBus from '@/plugins/event-bus'

export default {
  components: {
    EditCompany: () => import('@/modules/companies/components/EditCompany'),
    EditReceiptSettings: () => import('@/modules/companies/components/EditReceiptSettings'),
    CertificateUpload: () => import('@/modules/companies/components/CertificateUploadForm'),
    SetupWizard: () => import('@/modules/setup/components/SetupWizard')
  },
  inject: ['showLoader', 'hideLoader'],
  mixins: [applicationSettings],
  data: (vm) => ({
    companies: [],
    sortedCompanies: [],
    loading: false,
    menu: false,
    hideMenu: false,
    companyOptions: [
      { title: 'Dodaj certifikat', action: vm.addCertificate },
      { title: 'Postavke firme', action: vm.editCompany },
      { title: 'Postavke računa', action: vm.editReceiptSettings }
    ],
    unsubscribe: undefined,
    searchEmitDestination: 'companiesSearch',
    isMounted: false

  }),
  computed: {
    companiesTabs () {
      return [
        {
          title: this.$t('$vuetify.companiesTabs'),
          icon: 'mdi-briefcase-outline'
        }
      ]
    }
  },
  mounted () {
    EventBus.$on(this.searchEmitDestination, (searchObject) => {
      this.searchAll(searchObject.searchString)
    })

    const user = state.getUser()

    if (user && user.roles && (!user.roles.ADMIN && !user.roles.SUPERADMIN)) {
      this.hideMenu = true
    }
    // document.onkeydown = this.handleKeyEvents
    try {
      this.$refs.company0[0].$el.focus()
    } catch {
      setTimeout(() => {
        if (this.$refs.company0 && this.$refs.company0[0]) {
          this.$refs.company0[0].$el.focus()
        }
      }, 500)
    }

    this.isMounted = true

    // state.removeCurrentCompany()
  },
  created () {
    const docRef = df.doc(`user_companies/${auth.currentUser.uid}`)
    this.unsubscribe = docRef.onSnapshot((doc) => {
      const data = doc.data()
      if (data?.companies) {
        this.companies = Object.keys(data.companies)
          .map(key => data.companies[key])
          .filter(company => company?.resource_status === 'ACTIVE')
          .sort((a, b) => {
            if (a.id < b.id) return -1
            if (a.id > b.id) return 1
            return 0
          })
        this.applySearch()
      }
    })
  },
  beforeDestroy () {
    EventBus.$off(this.searchEmitDestination)
    this.unsubscribe()
  },
  methods: {
    getCompanyStatusClass (status) {
      if (!status) return 'status-default'
      if (status.toUpperCase() === 'OK') return 'status-ok'
      if (status.toUpperCase() === 'PROCESSING') return 'status-processing'
      return 'status-error'
    },
    applySearch (val) {
      const term = val !== undefined ? val : (this.search || '')
      const source = term !== ''
        ? this.companies.filter(c => c.name && c.name.toLowerCase().includes(term.toLowerCase()))
        : this.companies
      this.sortedCompanies = source.slice().sort((a, b) => a.name.localeCompare(b.name))
    },
    searchAll (val) {
      this.applySearch(val)
    },
    translateStatus (status) {
      if (!status) return ''
      if (status.toUpperCase() === 'OK' || status.toUpperCase() === 'PROCESSING') {
        return status
      } else {
        return this.$t('$vuetify.errors.errorTitle')
      }
    },
    getStatusTooltip (status) {
      if (!status) return ''
      if (status.toUpperCase() === 'OK' || status.toUpperCase() === 'PROCESSING') {
        return status
      } else if (status.toUpperCase() === 'DUPLICATE') {
        return this.$t('$vuetify.errors.duplicateCompany')
      } else {
        return status
      }
    },
    async openCompany (company) {
      if (company.status !== 'OK') {
        return
      }

      // Fetch additional company details including eRacun and cert_data
      try {
        const companyDetails = await df.doc(`companies/${company.id}`).get()
        if (companyDetails.exists) {
          const companyData = companyDetails.data()
          // Merge eRacun configuration from companies collection
          // eRacun structure: { provider: 'provider_name', active: true, email: 'email@example.com' }
          if (companyData.eracun) {
            company.eracun = companyData.eracun
          }
          // Merge cert_data from companies collection
          // cert_data structure: { common_name: 'FISKAL 1', exp_date: 1761661357, passwd: 'xxx', serial_num: 'xxx', start_date: 1603894957 }
          if (companyData.cert_data) {
            company.cert_data = companyData.cert_data
          }
        }
      } catch (error) {
        console.warn('Companies - Could not fetch company details:', error)
      }

      state.setCurrentCompany(company)
      // COMMENT - DODANO ZATO DA ODSETIRA LOKACIJU AKO JE ODABRANA NOVA FIRMA
      // SKLADISTE DOKUMENTI MENI OVISE O OVOME
      state.removePointOfSale()

      state.setAppTitle(company.name)

      this.$router.push({
        name: 'company',
        params: { companyId: company.id }
      }).catch(err => {
        // Ignore NavigationDuplicated error
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    },
    addCompany () {
      this.$refs.setupWizard.open()
    },
    editCompany (copmany) {
      this.$refs.editCompany.open(copmany)
    },
    editReceiptSettings (copmany) {
      this.$refs.editReceiptSettings.open(copmany)
    },
    refreshCompanies (company) {
      if (!this.search || company.name.toLowerCase().includes(this.search.toLowerCase())) {
        this.sortedCompanies = [...this.sortedCompanies, { name: company.name, status: 'PROCESSING' }]
          .sort((a, b) => a.name.localeCompare(b.name))
      }
    },
    refreshEditCompany (company) {
      this.sortedCompanies = this.sortedCompanies.map(com =>
        com.id === company.id ? { ...com, status: 'PROCESSING' } : com
      )
    },
    addCertificate (company) {
      this.$refs.certificateUpload.open(company)
    },
    handleKeyEvents (e) {
      e = e || window.event

      switch (e.keyCode) {
        case 70: {
          // F -> search
          if (!this.$refs.companies_search.isFocused) {
            this.$refs.companies_search.focus()
            e.preventDefault()
          }
          break
        }
      }
    }
  }
}
</script>
<style scoped>
/* Glass Morphism Card */
.glass-card {
  border-radius: 24px !important;
  background: #ffffff !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
  overflow: hidden;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              border-color 0.3s ease !important;
  cursor: pointer !important;
  max-width: 400px !important;
}

.glass-card::before {
  opacity: 0 !important;
}

.glass-card:hover {
  transform: translateY(-6px) scale(1.01);
  background: rgba(255, 255, 255, 0.85) !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12) !important;
  border-color: rgba(0, 179, 190, 0.4) !important;
}

/* Glass Header */
.glass-header {
  position: relative;
  height: 160px;
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%);
  overflow: hidden;
}

.header-gloss {
  position: absolute;
  top: 0;
  left: -100%;
  width: 60%;
  height: 100%;
  background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.22) 50%, transparent 100%);
  transition: left 0.5s ease;
  pointer-events: none;
  z-index: 1;
}

.glass-card:hover .header-gloss {
  left: 150%;
}

.glass-card:hover .glass-header {
  filter: brightness(1.1);
}

/* Logo Wrapper */
.logo-wrapper {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.2),
    inset 0 2px 4px rgba(255, 255, 255, 0.3);
  transition: transform 0.15s ease-out,
              background 0.15s ease,
              box-shadow 0.15s ease;
  overflow: hidden;
  margin-bottom: 8px;
  will-change: transform;
}

.glass-card:hover .logo-wrapper {
  transform: scale(1.1);
  background: rgba(255, 255, 255, 0.3);
  box-shadow:
    0 10px 28px rgba(0, 0, 0, 0.25),
    inset 0 2px 6px rgba(255, 255, 255, 0.4);
}

.company-logo {
  border-radius: 50%;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.logo-wrapper .v-icon {
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  transition: filter 0.15s ease;
}

.glass-card:hover .logo-wrapper .v-icon {
  filter: drop-shadow(0 5px 10px rgba(0, 0, 0, 0.35));
}

/* Status Badge */
.status-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.15s ease-out;
}

.status-ok {
  background: rgba(76, 175, 80, 0.9);
  color: white;
}

.status-processing {
  background: rgba(255, 152, 0, 0.9);
  color: white;
}

.status-error {
  background: rgba(244, 67, 54, 0.9);
  color: white;
}

.status-default {
  background: rgba(158, 158, 158, 0.9);
  color: white;
}

.glass-card:hover .status-badge {
  transform: scale(1.03);
}

/* Certificate Badge */
.certificate-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 10px;
  border-radius: 16px;
  font-size: 10px;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: transform 0.15s ease-out;
  display: flex;
  align-items: center;
}

.cert-active {
  background: rgba(76, 175, 80, 0.9);
  color: white;
}

.cert-inactive {
  background: rgba(244, 67, 54, 0.9);
  color: white;
}

.glass-card:hover .certificate-badge {
  transform: scale(1.03);
}

/* Content Section */
.glass-content {
  background: transparent;
  min-height: 105px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.content-text {
  flex: 1;
  min-width: 0;
  min-height: 72px;
}

.arrow-indicator {
  flex-shrink: 0;
  margin-left: 10px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.05);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.arrow-indicator .v-icon {
  color: #94A3B8 !important;
  transition: color 0.25s ease;
}

.glass-card:hover .arrow-indicator {
  background: #00BCD4;
  transform: translateX(2px);
}

.glass-card:hover .arrow-indicator .v-icon {
  color: white !important;
}

.company-name {
  font-size: 16px;
  font-weight: 600;
  color: #1E293B;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.15s ease;
  text-align: left;
  line-height: 1.4;
}

.glass-card:hover .company-name {
  color: #00b3be;
}

/* Info Rows */
.info-row {
  font-size: 13px;
  color: #64748B;
  transition: color 0.15s ease;
}

.info-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  line-height: 1;
}

.info-icon {
  color: #94A3B8 !important;
  flex-shrink: 0;
  line-height: 1;
}

.glass-card:hover .info-row {
  color: #475569;
}

.glass-card:hover .info-icon {
  color: #64748B !important;
}

/* Glass Divider */
.glass-divider {
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(0, 179, 190, 0.3) 50%,
    transparent 100%);
  height: 1px;
  border: none;
}

/* Actions */
.glass-actions {
  min-height: 52px;
  background: rgba(255, 255, 255, 0.5);
}

.cert-btn {
  background: rgba(255, 255, 255, 0.6) !important;
  transition: transform 0.15s ease-out,
              background 0.15s ease !important;
  will-change: transform;
}

.cert-btn:hover {
  background: rgba(255, 255, 255, 0.8) !important;
  transform: scale(1.1);
}

.menu-btn {
  background: transparent !important;
  transition: transform 0.2s ease, background 0.2s ease !important;
}

.menu-btn .v-icon {
  color: #94A3B8 !important;
  transition: color 0.2s ease;
}

.menu-btn:hover {
  background: rgba(0, 179, 190, 0.08) !important;
  transform: rotate(90deg);
}

.menu-btn:hover .v-icon {
  color: #00BCD4 !important;
}

/* Glass Menu */
.glass-menu {
  background: rgba(255, 255, 255, 0.98) !important;
  border-radius: 12px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
}

/* Legacy styles */
.card-title {
  display: block;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 220px;
}

.img {
  height: 100vh;
}

.one-liner-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.add-dashed-card {
  background-color: #FAFAFA;
  border: 2px dashed #CBD5E1;
  border-radius: 24px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.2s ease;
}

.add-dashed-card:hover {
  border-color: #00BCD4;
}

.add-dashed-card:hover .add-dashed-icon {
  color: #00BCD4 !important;
}

.add-dashed-card:hover .add-dashed-text {
  color: #00BCD4 !important;
}

.add-dashed-icon {
  font-size: 28px !important;
  color: #CBD5E1 !important;
  transition: color 0.2s ease;
}

.add-dashed-text {
  font-size: 14px;
  font-weight: 500;
  color: #94A3B8;
  transition: color 0.2s ease;
  text-align: center;
  padding: 0 16px;
}
</style>
